/**
 * AssessmentGapInterview — the drafter's gap interview (2026-07 redesign).
 *
 * Replaces the read-only follow-ups roll-up on the TOOL page (the drafts
 * detail page keeps AssessmentFollowUpsBlock for its read-only reuse).
 * Each flag:
 *   - is grouped by tier, safety first (attention treatment, pepper
 *     accent; never gold), then clinical completeness, then
 *     administrative;
 *   - jump-links to its section in the draft (anchor ids from
 *     assessmentSections.ts; unmapped sections stay roll-up-only);
 *   - carries its own small answer field.
 *
 * One action, "Fill these in", sends the answered flags to the targeted
 * patch pass (the page owns the mutation); a progress chip counts
 * answers. Answer state lives here, keyed by flag identity, so answers
 * for flags a patch run resolves simply fall away.
 *
 * First-person register throughout (founder ruling): the flags speak as
 * "I"; this chrome never says "the AI".
 */

import { type ReactNode, useState } from 'react';
import type { AssessmentFlagTier, AssessmentFollowUpQuestion, AssessmentGapAnswer } from '@/lib/api';
import { sectionAnchorId, sectionKeyForFlag } from '@/lib/assessmentSections';
import { Button } from '@/components/primitives/Button';

export interface AssessmentGapInterviewProps {
  questions: readonly AssessmentFollowUpQuestion[];
  /** Fires with the answered flags; the page owns the patch mutation. */
  onFillIn: (answers: AssessmentGapAnswer[]) => void;
  /** True while the patch call runs (disables the controls). */
  pending: boolean;
  /** Page-mapped error copy, or null. */
  error: string | null;
}

const TIER_ORDER: readonly AssessmentFlagTier[] = ['safety', 'completeness', 'administrative'];

const TIER_LABELS: Record<AssessmentFlagTier, string> = {
  safety: 'Safety',
  completeness: 'Clinical completeness',
  administrative: 'Administrative',
};

/** Legacy drafts (and defensive parses) may lack a tier. */
function tierOf(q: AssessmentFollowUpQuestion): AssessmentFlagTier {
  return q.tier ?? 'completeness';
}

function flagKey(q: AssessmentFollowUpQuestion): string {
  return `${q.section}::${q.question}`;
}

const FIELD_CLS =
  'mt-1.5 block w-full rounded-[10px] border border-sage-400/25 bg-paper px-3 py-2 text-sm text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';

export function AssessmentGapInterview({
  questions,
  onFillIn,
  pending,
  error,
}: AssessmentGapInterviewProps): ReactNode {
  // Keyed by flag identity: answers for flags a patch run resolves fall
  // away with their flags; unanswered flags keep their draft text.
  const [answers, setAnswers] = useState<Record<string, string>>({});

  if (questions.length === 0) return null;

  const answered = questions.filter((q) => (answers[flagKey(q)] ?? '').trim().length > 0);

  const groups = TIER_ORDER.map((tier) => ({
    tier,
    items: questions
      .map((q, index) => ({ q, index }))
      .filter(({ q }) => tierOf(q) === tier),
  })).filter((g) => g.items.length > 0);

  const jumpTo = (section: string): void => {
    const key = sectionKeyForFlag(section);
    if (key === null) return;
    const el = document.getElementById(sectionAnchorId(key));
    // jsdom has no scrollIntoView; browsers do.
    el?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const handleFillIn = (): void => {
    if (answered.length === 0 || pending) return;
    onFillIn(
      answered.map((q) => ({
        section: q.section,
        question: q.question,
        answer: answers[flagKey(q)]!.trim(),
      })),
    );
  };

  const headingStyle = { color: '#7a5a1f' } as const;

  return (
    <section
      className="rounded-lg border-l-4 border-y border-r border-sage-800/[8%] p-4 space-y-3"
      style={{ backgroundColor: '#FBF4E8', borderLeftColor: 'var(--color-warm-400)' }}
      data-testid="assessment-gap-interview"
      aria-label="Follow-up questions"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold" style={headingStyle}>
          Before you finalize
        </h3>
        <span
          className="rounded-pill bg-paper px-2.5 py-0.5 text-xs font-semibold text-sage-800"
          data-testid="assessment-gap-progress"
        >
          {answered.length} of {questions.length} answered
        </span>
      </div>
      <p className="text-xs leading-relaxed text-ink-soft">
        Answer any of these and the answers go into the right sections; the rest of the report
        stays as it is.
      </p>

      {groups.map(({ tier, items }) => (
        <div key={tier} data-testid={`assessment-gap-tier-${tier}`}>
          <p
            className={
              'text-[11.5px] font-semibold uppercase tracking-[0.16em] ' +
              (tier === 'safety' ? 'text-pepper' : 'text-sage-400')
            }
          >
            {TIER_LABELS[tier]}
          </p>
          <ul className="mt-1.5 space-y-2.5 list-none pl-0">
            {items.map(({ q, index }) => {
              const key = flagKey(q);
              const anchored = sectionKeyForFlag(q.section) !== null;
              return (
                <li
                  key={key}
                  className={
                    'rounded-[10px] bg-paper/70 px-3 py-2.5 ' +
                    (tier === 'safety' ? 'border-l-2 border-pepper' : '')
                  }
                  data-testid={`assessment-gap-flag-${index}`}
                >
                  <div className="text-sm leading-relaxed text-ink">
                    {anchored ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="mr-1 !px-1.5 underline underline-offset-2"
                        onClick={() => jumpTo(q.section)}
                        data-testid={`assessment-gap-jump-${index}`}
                        aria-label={`Jump to ${q.section}`}
                      >
                        {q.section}
                      </Button>
                    ) : (
                      <span className="mr-1 font-semibold" style={headingStyle}>
                        {q.section}:
                      </span>
                    )}
                    <span>{q.question}</span>
                  </div>
                  <textarea
                    rows={1}
                    className={FIELD_CLS}
                    placeholder="Your answer (leave blank to skip)"
                    value={answers[key] ?? ''}
                    onChange={(e) => setAnswers((prev) => ({ ...prev, [key]: e.target.value }))}
                    disabled={pending}
                    aria-label={`Answer: ${q.question}`}
                    data-testid={`assessment-gap-answer-${index}`}
                  />
                </li>
              );
            })}
          </ul>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleFillIn}
          loading={pending}
          disabled={pending || answered.length === 0}
          data-testid="assessment-gap-fill-in"
        >
          {pending ? 'Working the answers in…' : 'Fill these in'}
        </Button>
        {answered.length === 0 && !pending && (
          <span className="text-xs text-ink-soft">Answer at least one flag to run this.</span>
        )}
      </div>

      {error !== null && (
        <p
          className="rounded-[12px] border border-warm-200 bg-warm-50 px-3 py-2 text-sm text-warm-700"
          role="alert"
          data-testid="assessment-gap-error"
        >
          {error}
        </p>
      )}
    </section>
  );
}
